/* eslint-disable @typescript-eslint/no-var-requires */
import { FC, useMemo, useState, useEffect } from 'react';
import { Box, Divider, IconButton, Stack } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ButtonsContainer, TooltipPopperStylesSmall } from './checklist.style';
import CustomToolTip from 'components/Tooltip';
import ZoomInIcon from 'components/Icons/zoomInIcon';
import ZoomOutIcon from 'components/Icons/zoomOutIcon';
import mime from 'mime';
const DocViewer = require('@cyntler/react-doc-viewer').default;
const { DocViewerRenderers } = require('@cyntler/react-doc-viewer');

const ChecklistAttachmentPreview: FC<any> = ({
  attachments,
  hideAttachmentPreview,
  selectedAttachment,
}) => {
  const [currentDocument, setCurrentDocument] = useState();
  const [activeDocument, setActiveDocument] = useState<string>('');
  const [zoomLevel, setZoomLevel] = useState(1);

  const allDocuments = useMemo(
    () =>
      attachments?.map((a) => ({
        uri: `${a.url}?timestamp=${new Date().getTime()}`,
      })),
    [attachments],
  );

  useEffect(() => {
    if (allDocuments) {
      const current = allDocuments[selectedAttachment];
      setActiveDocument(current?.uri ?? '');
      setCurrentDocument(current);
    }
  }, [allDocuments, selectedAttachment]);

  const handleZoomIn = () => {
    const newZoomLevel = Math.min(zoomLevel + 0.2, 3);
    setZoomLevel(newZoomLevel);
  };

  const handleZoomOut = () => {
    const newZoomLevel = Math.max(zoomLevel - 0.2, 1);
    setZoomLevel(newZoomLevel);
  };

  const isImage = useMemo(() => {
    try {
      if (activeDocument) {
        const pathname = new URL(activeDocument).pathname;
        const mimeType = mime.getType(pathname);

        return mimeType?.startsWith('image/');
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [activeDocument]);

  return (
    <Box
      sx={{
        '& div#react-doc-viewer': {
          height: '100%',
          bgcolor: 'white',
        },
        '& #image-renderer': {
          overflow: 'hidden',
          '& img': {
            transform: `scale(${zoomLevel})`,
          },
        },
        '& div#proxy-renderer': {
          backgroundColor: 'white',
        },
        '& div#no-renderer': {
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          '& a': {
            marginTop: '12px',
            borderRadius: '8px',
            bgcolor: 'rgba(104,104,254,1)',
            color: 'white',
            fontSize: '16px',
          },
        },
        '& div#header-bar': {
          width: 'calc(100% - 50px)',
          paddingRight: '0px',
          '& div#doc-nav': {
            marginRight: '4px',
          },
        },
        '& #doc-nav-prev': {
          bgcolor: 'rgba(104,104,254,1)',
        },
        '& #doc-nav-next': {
          bgcolor: 'rgba(104,104,254,1)',
        },
      }}
      height="100vh"
    >
      {isImage && (
        <Stack
          sx={{
            position: 'fixed',
            right: attachments?.length > 1 ? '260px' : '60px',
            top: '5px',
            zIndex: 2,
          }}
        >
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <ButtonsContainer>
              <>
                <CustomToolTip
                  title="Zoom Out"
                  arrow={false}
                  placement="top"
                  PopperProps={{
                    sx: TooltipPopperStylesSmall,
                  }}
                >
                  <IconButton onClick={handleZoomOut}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </CustomToolTip>
                <CustomToolTip
                  title="Zoom In"
                  arrow={false}
                  placement="top"
                  PopperProps={{
                    sx: TooltipPopperStylesSmall,
                  }}
                >
                  <IconButton onClick={handleZoomIn}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </CustomToolTip>
              </>
            </ButtonsContainer>
            <Divider
              orientation="vertical"
              style={{ height: 30, marginRight: 4, marginLeft: 12 }}
            />
          </Stack>
        </Stack>
      )}
      <Stack
        borderRadius={'100px'}
        width={'32px'}
        height="32px"
        sx={{
          position: 'fixed',
          right: '12px',
          top: '9px',
          cursor: 'pointer',
        }}
        onClick={hideAttachmentPreview}
        alignItems={'center'}
        justifyContent={'center'}
        bgcolor={'#eee'}
      >
        <CloseRoundedIcon sx={{ fontSize: '24px' }} />
      </Stack>

      <DocViewer
        documents={allDocuments}
        pluginRenderers={DocViewerRenderers}
        initialActiveDocument={currentDocument}
        onDocumentChange={(document) => {
          setZoomLevel(1);
          setActiveDocument(document?.uri);
        }}
      />
    </Box>
  );
};

export default ChecklistAttachmentPreview;
